import React, { useCallback, useMemo, useState } from 'react';
import { Button, Text, useToast, VStack, Spinner } from '@televet/kibble-ui';
import { useHistory } from 'react-router-dom';
import {
  ChannelStatusAction,
  ChannelViewChannelMemberFragment,
  useGetPetParentActiveChannelMembersQuery,
  useGetArchivedOverlayChannelStatusesLazyQuery,
  useUpdateArchivedOverlayChannelStatusMutation,
} from 'shared/types/graphql';
import { replaceParam, RouteDefinitions } from 'routes';
import { getChannelParticipants } from 'pages/Conversations/utils';
import { GraphQLFetchPolicies } from 'shared/enums';

interface ArchivedOverlayProps {
  channelMembers?: ChannelViewChannelMemberFragment[] | null;
  channelId?: string | undefined;
  currentClinicId?: string;
  isTeamChannel?: boolean;
  channelStatusAction?: ChannelStatusAction;
}

export const ArchivedOverlay = ({
  channelMembers,
  channelId,
  currentClinicId,
  isTeamChannel,
  channelStatusAction,
}: ArchivedOverlayProps): JSX.Element => {
  const history = useHistory();
  const [isReopeningChannel, setIsReopeningChannel] = useState(false);
  const toast = useToast();
  const [getChannelStatuses] = useGetArchivedOverlayChannelStatusesLazyQuery();
  const [updateChannelStatus] = useUpdateArchivedOverlayChannelStatusMutation();
  const { channelPetParentIds } = getChannelParticipants(channelMembers || []);

  const { data: activeChannelMembers, loading: isLoadingActiveChannelMembers } =
    useGetPetParentActiveChannelMembersQuery({
      variables: { clinicPetParentIds: channelPetParentIds },
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });

  const currentlyActiveChannelId: string | undefined = useMemo(() => {
    return activeChannelMembers?.findManyChannelMember?.[0]?.channelId ?? undefined;
  }, [activeChannelMembers]);

  const navigateToActiveParentChannel = useCallback(() => {
    if (currentlyActiveChannelId) {
      history.push(`${replaceParam(RouteDefinitions.Conversations, ':channelId', currentlyActiveChannelId)}`);
    } else {
      toast({
        status: 'info',
        description: 'Ooops, we were unable to find the right conversation.',
      });
    }
  }, [currentlyActiveChannelId, history, toast]);

  const handleReopenChannel = useCallback(async (): Promise<void> => {
    setIsReopeningChannel(true);

    try {
      const { data: statuses } = await getChannelStatuses({
        variables: {
          where: {
            clinicSetting: {
              clinic: {
                id: {
                  equals: currentClinicId,
                },
              },
            },
            deletedOn: { equals: null },
            channelStatusAction: {
              in: [ChannelStatusAction.Active],
            },
          },
        },
      });

      if (statuses?.findManyChannelStatus) {
        await updateChannelStatus({
          variables: {
            where: { id: channelId },
            data: { channelStatus: { connect: { id: statuses?.findManyChannelStatus[0]?.id } } },
          },
        });
      }
    } catch (e) {
      toast({
        status: 'error',
        description: 'Unable to re-open conversation.',
      });
    }

    setIsReopeningChannel(false);
  }, [currentClinicId, getChannelStatuses, toast, channelId, updateChannelStatus]);

  return (
    <VStack
      px={4}
      py={12}
      borderTop="1px"
      bg="background.subtle"
      borderColor="border.default"
      align="center"
      justify="center"
    >
      {isTeamChannel ? (
        <Text align="center">This channel has been archived.</Text>
      ) : isLoadingActiveChannelMembers ? (
        <Spinner />
      ) : (
        <>
          {currentlyActiveChannelId && currentlyActiveChannelId !== channelId ? (
            <VStack spacing={4}>
              <Text align="center">
                An active conversation with one or more clients in this conversation already exists.
              </Text>
              <Button onClick={navigateToActiveParentChannel}>Go to Active Conversation</Button>
            </VStack>
          ) : (
            <VStack spacing={4}>
              <Text align="center">
                This conversation has been{' '}
                {channelStatusAction === ChannelStatusAction.Inactive ? 'closed' : 'archived'}.
              </Text>
              <Button variant="secondary" onClick={handleReopenChannel} isLoading={isReopeningChannel}>
                Reopen Conversation
              </Button>
            </VStack>
          )}
        </>
      )}
    </VStack>
  );
};
